/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import Box from "@mui/material/Box";
import {
  AUTH_TOKEN_TYPES,
  IAuthTokenWithType,
  useTokenManagementService,
} from "../../../services/registry/TokenManagementService";

export interface AgentTokenPopupProps {
  visible: boolean;
  baseURL: string;
  onClose: () => void;
}

const TWELVE_HOURS_IN_SECONDS = 12 * 3600;
const TOKEN_LENGTH = 32;

const generateToken = (length: number): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

export const AgentTokenPopup = ({ visible, baseURL, onClose }: AgentTokenPopupProps) => {
  const tokenService = useTokenManagementService();
  const [currentToken, setCurrentToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);

  const exportText =
    currentToken !== undefined ? `export ENVIRONMENT_BASE_URL=${baseURL}\nexport TFK_AI_AGENT_TOKEN=${currentToken}` : "";

  const cleanupAndProvision = async () => {
    setLoading(true);
    setError(undefined);
    setCopied(false);
    try {
      const { list } = await tokenService.getTokens();
      const agentTokens: IAuthTokenWithType[] = list.filter(
        (t: IAuthTokenWithType) => t.tokenType === AUTH_TOKEN_TYPES.AI_AGENT,
      );

      const now = Math.floor(Date.now() / 1000);

      for (const t of agentTokens) {
        const isExpired = t.expiresAt < now;
        const isTooLongLived = t.expiresAt > now + TWELVE_HOURS_IN_SECONDS;
        if (isExpired || isTooLongLived) {
          try {
            await tokenService.removeToken(AUTH_TOKEN_TYPES.AI_AGENT, t.token);
          } catch {
            // best-effort cleanup
          }
        }
      }

      const newToken = generateToken(TOKEN_LENGTH);
      const expiresAt = now + TWELVE_HOURS_IN_SECONDS;
      await tokenService.createToken(AUTH_TOKEN_TYPES.AI_AGENT, newToken, expiresAt);
      setCurrentToken(newToken);
    } catch (e: any) {
      setError(e.debugMessage || "Failed to provision agent token.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setCurrentToken(undefined);
      cleanupAndProvision();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleRegenerate = async () => {
    setLoading(true);
    setError(undefined);
    setCopied(false);
    try {
      if (currentToken) {
        await tokenService.removeToken(AUTH_TOKEN_TYPES.AI_AGENT, currentToken);
      }
      const now = Math.floor(Date.now() / 1000);
      const newToken = generateToken(TOKEN_LENGTH);
      const expiresAt = now + TWELVE_HOURS_IN_SECONDS;
      await tokenService.createToken(AUTH_TOKEN_TYPES.AI_AGENT, newToken, expiresAt);
      setCurrentToken(newToken);
    } catch (e: any) {
      setError(e.debugMessage || "Failed to regenerate agent token.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select the text so the user can copy manually
    }
  };

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>AI Agent Token</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ color: "error.main", py: 2 }}>{error}</Box>
        ) : (
          <TextField
            fullWidth
            multiline
            rows={2}
            value={exportText}
            slotProps={{
              input: {
                readOnly: true,
                sx: {
                  fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
                  fontSize: "13px",
                  backgroundColor: "#000",
                  color: "#0f0",
                },
              },
            }}
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRegenerate} disabled={loading} startIcon={<RefreshIcon />}>
          Regenerate
        </Button>
        <Button onClick={handleCopy} disabled={loading || !currentToken} startIcon={<ContentCopyIcon />}>
          {copied ? "Copied!" : "Copy"}
        </Button>
        <Button onClick={onClose} color="error">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
